//remove isGradient background

import { DS } from '@/design-system'
import { getDSColorWithOpacity } from '@/design-system/Palette'

import type { DSColor } from '@ppwr/poker'

type BackgroundType = 'color' | 'gradient' | 'image'

import DefaultBackgroundImage from '@/components/Mini/assets/Daily10_BKG.svg' //daily10
import PurpleBackgroundImage from '@/components/Mini/assets/CTH_BKG.svg' //cth
import GreenBackgroundImage from '@/components/Mini/assets/PocketPick_BKG.svg' //pocketpick

//import DefaultBackgroundImage from '@/components/Mini/assets/Daily10_BKG.png' //daily10
//import PurpleBackgroundImage from '@/components/Mini/assets/CTH_BKG.png' //cth
//import GreenBackgroundImage from '@/components/Mini/assets/PocketPick_BKG.png' //pocketpick


function createTheme(override={}){
  let theme;

  const base = {
    isDark: false, //properties
    isGradientBackground: false,
    backgroundType: "color" as BackgroundType,
    background: DS.palette['Brand/Cream'], //ui   
    bgGradient: ['#F6F2E9', '#F6F2E9', '#F6F2E9'] as [string, string, string],
    backgroundImage: DefaultBackgroundImage,
    surface: DS.palette['Global/White'], 
    surfaceBorder: DS.palette['Brand/Light Grey'],
    rowBackground: DS.palette['Brand/Cream'], 
    accent: DS.palette['Brand/Maroon'],  
    success: DS.palette['Utility/Success'],
    danger: DS.palette['Utility/Warning'],
    accentText: DS.palette['Global/White'], 
    textDefault: DS.palette['Global/Black'],
    textDefaultDim: DS.palette['Brand/Dark Grey'],
    textDefaultSlightDim: DS.palette['Brand/Dark Grey'],
    //textDim: DS.palette['Global/Black'],
    textPrimary: DS.palette['Global/Black'],
    icon: 'Global/Black' as DSColor,
    //statusBar: "light"

    //game - pocketPick
    slotEdge: '#C9A24B',
    slotSurface: getDSColorWithOpacity('Global/White', 0.04),
    slotQ: 'rgba(201,162,75,0.5)',
    bestGlow: '#F2C879',
    // Strength ramp for the win-% numbers: green (best) → red (worst), distinct hues.
    // The best card is still marked by its gold glow ring, so green = "strongest", not "the answer".
    tier: {
      //premium: { label: 'Premium', color: '#34C759' }, // green — strongest
      premium: { label: 'Premium', color: DS.palette['Utility/Success'] }, // green — strongest
      strong: { label: 'Strong', color: '#C9D44A' }, // lime
      marginal: { label: 'Marginal', color: '#F0922E' }, // orange
      //muck: { label: 'Muck', color: '#E5484D' } // red — weakest
      muck: { label: 'Muck', color: DS.palette['Utility/Warning'] } // red — weakest
    },

    //game - cth
    //feltRail: '#0E5C43',
    feltRail: '#663CE5',
    //feltBed: '#0A3D2C',
    feltBed: '#230D93',
    highlightGold: '#F2C879', // gold ring on the correct card(s) — matches Pocket Pick
    glowWin: '#4ADE80', // bright green that highlights against the dark felt
    glowLose: '#FF3333', // bright true red
    feltSpotlight: '#F6EEDA', // warm light; opacity applied via stopOpacity
    ringGray: '#D2D6D4',
  }

  theme = Object.assign({}, base, override)

  return theme;
}

//default
const defaultTheme = createTheme();

const defaultImageTheme=createTheme({
  isDark: true,
  backgroundType: "image" as BackgroundType,
  background: DS.palette['Brand/Cream'],
  icon: 'Global/Black',
  rowBackground: '',
})

//felt
const greenTheme = createTheme({
  isDark: true,
  isGradientBackground: true,
  backgroundType: "gradient",
  background: '#05140E',
  bgGradient: ['#0B2E22', '#082017', '#05140E'],
  //bgGradient: ["pink", "orange", "yellow"], //test
  surface: 'rgba(255,255,255,0.05)',
  surfaceBorder: 'rgba(232,224,204,0.18)',
  rowBackground: 'rgba(255,255,255,0.04)',
  accent: '#E8E0CC',
  success: '#34C759',
  danger: '#E5484D',
  accentText: '#082017',
  textDefault: "#FFFFFF", 
  textDefaultDim: 'rgba(255,255,255,0.6)',
  textDefaultSlightDim: 'rgba(255,255,255,0.85)',
  //textDim: 'rgba(244,241,232,0.6)',
  ////textPrimary: '#F4F1E8'
  //textPrimary: DS.palette['Global/Black']
  icon: 'Global/White'
});

const greenImageTheme = createTheme({
  isDark: true,
  isGradientBackground: true,
  background: DS.palette['Brand/Cream'],
  bgGradient: ['#0B2E22', '#082017', '#05140E'],
  backgroundType: "image",
  backgroundImage: GreenBackgroundImage,
  //bgGradient: ["pink", "orange", "yellow"], //test
  //surface: 'rgba(255,255,255,0.05)',
  //surfaceBorder: 'rgba(232,224,204,0.18)',
  surface: DS.palette['Global/White'], 
  surfaceBorder: DS.palette['Brand/Light Grey'],
  //rowBackground: 'rgba(255,255,255,0.04)',
  rowBackground: '',
  //accent: '#E8E0CC',
  accent: DS.palette['Brand/Green'],
  success: '#34C759',
  danger: '#E5484D',
  //accentText: '#082017',
  accentText: DS.palette['Global/White'],
  //textDefault: "#FFFFFF", 
  //textDefaultDim: 'rgba(255,255,255,0.6)',
  //textDefaultSlightDim: 'rgba(255,255,255,0.85)',
  textDefault: DS.palette['Global/Black'],
  textDefaultDim: getDSColorWithOpacity('Global/Black', 0.6),
  textDefaultSlightDim: getDSColorWithOpacity('Global/Black', 0.85),
  //textDim: 'rgba(244,241,232,0.6)',
  ////textPrimary: '#F4F1E8'
  //textPrimary: DS.palette['Global/Black']
  icon: 'Global/Black'
});

//felt
const purpleTheme = createTheme({
  isDark: true,
  isGradientBackground: true,
  backgroundType: "gradient",
  background:'#0B0307',
  bgGradient: ['#3A0A22', '#1A0510', '#0B0307'],
  surface: 'rgba(255,255,255,0.05)',
  surfaceBorder: 'rgba(217,180,90,0.15)',
  rowBackground: getDSColorWithOpacity('Global/White', 0.04), //glass
  accent: '#D9B45A',
  success: '#34C759',
  danger: '#E5484D',
  accentText: '#1A0510',
  textDefault: DS.palette['Global/White'],
  textDefaultDim: getDSColorWithOpacity('Global/White', 0.6),
  textDefaultSlightDim: getDSColorWithOpacity('Global/White', 0.85),
  //textDim: getDSColorWithOpacity('Global/White', 0.6),
  //textPrimary: DS.palette['Global/Black']
  icon: 'Global/White'
})

const purpleImageTheme = createTheme({
  isDark: true,
  isGradientBackground: true,
  backgroundType: "image",
  background: DS.palette['Brand/Cream'],
  bgGradient: ['#3A0A22', '#1A0510', '#0B0307'],
  backgroundImage: PurpleBackgroundImage,
  //surface: 'rgba(255,255,255,0.05)',
  //surfaceBorder: 'rgba(217,180,90,0.15)',
  surface: DS.palette['Global/White'], 
  surfaceBorder: DS.palette['Brand/Light Grey'],
  //rowBackground: getDSColorWithOpacity('Global/White', 0.04), //glass
  rowBackground: '',
  //accent: '#D9B45A',
  accent: '#230D93',
  success: '#34C759',
  danger: '#E5484D',
  //accentText: '#1A0510',
  accentText: DS.palette['Global/White'],
  //textDefault: DS.palette['Global/White'],
  //textDefaultDim: getDSColorWithOpacity('Global/White', 0.6),
  //textDefaultSlightDim: getDSColorWithOpacity('Global/White', 0.85),
  textDefault: DS.palette['Global/Black'],
  textDefaultDim: getDSColorWithOpacity('Global/Black', 0.6),
  textDefaultSlightDim: getDSColorWithOpacity('Global/Black', 0.85),
  //textDim: getDSColorWithOpacity('Global/White', 0.6),
  //textPrimary: DS.palette['Global/Black']
  icon: 'Global/Black'
})

type ThemeTier = keyof typeof defaultTheme.tier

export { defaultTheme, greenTheme, purpleTheme, defaultImageTheme, greenImageTheme, purpleImageTheme, ThemeTier }
